import { to12HourTime, to24HourTime } from "@/util/dateUtil";
import { DropdownGroup, DropdownButton, DropdownItems, DropdownItem } from "@/component/input";

import { TimeValue } from "../type";


const MERIDIEM_OPTIONS: ("AM" | "PM")[] = ["AM", "PM"];
const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => i);

interface TimeInputProps {
    value: TimeValue;
    "aria-label"?: string;
    onChange: (time: TimeValue) => void;
}

const TimeInput: React.FC<TimeInputProps> = (props) => {
    const { value: time, "aria-label": ariaLabel, onChange } = props;

    const hour = to12HourTime(time.hour);
    const minute = time.minute;
    const meridiem = time.hour >= 12 ? "PM" : "AM";

    const onChangeMeridiem = (meridiem: "AM" | "PM") => {
        onChange({
            ...time,
            hour: to24HourTime(hour, meridiem)
        });
    };

    const onChangeHour = (hour: number) => {
        onChange({
            ...time,
            hour: to24HourTime(hour, meridiem)
        });
    };

    const onChangeMinute = (minute: number) => {
        onChange({ ...time, minute });
    };

    return (
        <div role="group"
             aria-label={ariaLabel}
             className="w-full flex items-center gap-1.5"
        >
            <TimeInputDropdown options={HOUR_OPTIONS}
                               aria-label="시"
                               value={hour}
                               onChange={onChangeHour}
            />

            <span aria-hidden="true"
                  className="text-[12px] font-[300] text-black-400"
            >
                :
            </span>

            <TimeInputDropdown options={MINUTE_OPTIONS}
                               aria-label="분"
                               value={minute}
                               onChange={onChangeMinute}
            />

            <span aria-hidden="true"
                  className="text-[12px] font-[300] text-black-400"
            >
                :
            </span>

            <TimeInputDropdown options={MERIDIEM_OPTIONS}
                               aria-label="오전/오후"
                               value={meridiem}
                               onChange={onChangeMeridiem}
            />
        </div>
    );
};

interface TimeInputDropdownProps<T> {
    value: T;
    options: T[];
    "aria-label"?: string;
    onChange: (value: T) => void;
}

const TimeInputDropdown = <T extends string | number>(props: TimeInputDropdownProps<T>) => {
    const { value, options, "aria-label": ariaLabel, onChange } = props;

    return (
        <DropdownGroup width={54}
                       className="h-6"
                       aria-label={ariaLabel}
        >
            <DropdownButton variant="secondary"
                            className="size-full py-1 pl-2.5 pr-1.5 justify-between text-[12px] text-black-400 font-[300] data-[active]:bg-transparent"
            >
                {value}
            </DropdownButton>
            <DropdownItems variant="secondary"
                           maxHeight={224}
                           className="mt-1 z-[1000]"
            >
                {options.map(option => (
                    <DropdownItem key={option}
                                  className="p-0 pr-3 text-end"
                                  onClick={() => onChange(option)}
                    >
                        {option}
                    </DropdownItem>
                ))}
            </DropdownItems>
        </DropdownGroup>
    );
};

export default TimeInput;
